import { useEffect, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { createFormField, FormFieldBlueprint } from '@backstage/plugin-scaffolder-react/alpha';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type Service = {
  slug: string;
  name: string;
  organization_slug: string | null;
  organization_name: string | null;
};

type Opts = {
  // Nome do campo do form que tem o slug do órgão escolhido.
  // Default: 'organization_slug' (= o que o XviaOrganizationPicker grava).
  organizationField?: string;
};

const Component = (
  props: FieldExtensionComponentProps<string, Opts>,
) => {
  const { onChange, rawErrors, required, formData, schema, formContext, uiSchema } =
    props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const opts = (uiSchema?.['ui:options'] ?? {}) as Opts;
  const organizationField = opts.organizationField ?? 'organization_slug';
  const orgSlug = (formContext?.formData?.[organizationField] as string) ?? '';

  const [items, setItems] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refaz o fetch toda vez que o orgSlug muda. Se ainda não tem órgão,
  // não busca nada (UX: "primeiro escolha um órgão, depois aparece a lista").
  useEffect(() => {
    let cancelled = false;
    if (!orgSlug) {
      setItems([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('xvia-cms-api');
        const r = await fetchApi.fetch(
          `${baseUrl}/services?organization=${encodeURIComponent(orgSlug)}`,
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
        const data = (await r.json()) as { services: Service[] };
        if (!cancelled) setItems(data.services ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'erro ao listar serviços');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, orgSlug]);

  // Se o admin trocar de órgão e o serviço escolhido sumir da nova lista,
  // limpa o form value pra evitar inconsistência.
  useEffect(() => {
    if (!formData) return;
    if (loading) return;
    if (items.length === 0) return;
    const stillValid = items.some(s => s.slug === formData);
    if (!stillValid) onChange('');
  }, [items, formData, loading, onChange]);

  const label = schema.title ?? 'Serviço da Carta';
  const hasError = !!rawErrors?.length;

  if (!orgSlug) {
    return (
      <FormControl margin="normal" required={required} fullWidth disabled>
        <InputLabel shrink>{label}</InputLabel>
        <Select value="" displayEmpty>
          <MenuItem value="">(escolha um órgão primeiro)</MenuItem>
        </Select>
        <FormHelperText>
          {schema.description ??
            'Lista carregada do CMS X-Via após escolher o órgão.'}
        </FormHelperText>
      </FormControl>
    );
  }

  if (loading) {
    return (
      <FormControl margin="normal" fullWidth>
        <InputLabel>{label}</InputLabel>
        <CircularProgress size={20} />
      </FormControl>
    );
  }

  let helperText = schema.description ?? 'Serviço carregado do CMS X-Via';
  if (error) {
    helperText = `Falha: ${error}`;
  } else if (items.length === 0) {
    helperText = 'Nenhum serviço cadastrado nesse órgão ainda.';
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={hasError || !!error}
      fullWidth
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        <MenuItem value="">(escolha um serviço)</MenuItem>
        {items.map(s => (
          <MenuItem key={s.slug} value={s.slug}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};

export const XviaServicePickerExtension = FormFieldBlueprint.make({
  name: 'xvia-service-picker',
  params: {
    field: async () => createFormField({
      name: 'XviaServicePicker',
      component: Component,
    }),
  },
});
